/**
 * Shared Sentry options — strict no-PII contract (the platform handles workers'
 * health data, GDPR art. 9). Events carry stack traces and masked messages only:
 * no request bodies, no cookies, no user identifiers.
 *
 * This is the browser/edge half of the same rule the backend enforces in
 * `backend/app/core/log_sanitizer.py`. The two implementations are native to
 * their own regex engine — `\1` vs `$1`, inline flags and `\b` all differ — and
 * are held together by `shared/pii-test-vectors.json`, the corpus both test
 * suites read. Adding a shape on one side means adding its vector there, which
 * turns the other side red until it covers it too.
 *
 * Sentry stays disabled unless NEXT_PUBLIC_SENTRY_DSN is set. Note it is a
 * build-arg (`ci.yml`), inlined into the client bundle: the day the GitHub
 * variable is filled in, this file is what stands between an exception and a
 * third party.
 */
import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/nextjs';

/**
 * Order matters: the widest redactions run first, so the narrower patterns never
 * have to look inside a region that is already gone. Mirrors `_SUBSTITUTIONS`.
 *
 * Every replacement must be a fixed point — `beforeSend` can run over text that
 * has already been masked (a breadcrumb built from an error message), and a
 * substitution whose output re-matches would corrode the line on every pass.
 */
const SUBSTITUTIONS: ReadonlyArray<readonly [RegExp, string]> = [
  // SQLAlchemy / Postgres shapes. They originate on the server, but an API error
  // message relayed to the client carries them verbatim.
  [/\[parameters: [^\n]*\]/g, '[parameters: ***]'],
  [/(Key \([^)\n]*\)=\()[^)\n]*\)/g, '$1***)'],
  [/(Failing row contains )\([^\n]*/g, '$1(***)'],
  // Credentials inside a URL
  [/(\/\/[^\s/:@]+):[^\s/@]+@/g, '$1:***@'],
  // The supplier portal link: its token is the fragment, so the page URL carries it
  // until the page strips it. To end of line, like the backend.
  [/(supplier-portal#)[^\n]*/g, '$1***'],
  // JWTs — access/refresh tokens, impersonation grants
  [/\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]*)?/g, '***'],
  [/\b(Bearer\s+)[A-Za-z0-9._\-+/=]+/gi, '$1***'],
  // Secret-bearing params in a query string
  [/((?:token|password|passwd|pwd|secret|api_key)=)[^\s&"']+/gi, '$1***'],
  // Emails keep first char + domain: enough to correlate a support ticket
  [/([A-Za-z0-9._%+-])[A-Za-z0-9._%+-]*@([A-Za-z0-9.-]+\.[A-Za-z]{2,})/g, '$1***@$2'],
  // Codice fiscale, then IBAN. Specific enough that false positives are confined
  // to same-length tokens, and over-masking is the safe direction.
  [/\b[A-Za-z]{6}\d{2}[A-Za-z]\d{2}[A-Za-z]\d{3}[A-Za-z]\b/g, '***'],
  [/\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g, '***'],
];

export function maskText(text: string): string {
  return SUBSTITUTIONS.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), text);
}

/**
 * Masks the strings inside an arbitrary payload (breadcrumb `data`, `extra`).
 *
 * Recursive, but depth-capped: these come from Sentry's own integrations and are
 * shallow, while an event that arrives cyclic or 200 levels deep must not take
 * down `beforeSend` — a scrubber that throws sends the event unscrubbed.
 */
function maskValue(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') return maskText(value);
  if (depth >= 4 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => maskValue(item, depth + 1));
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, maskValue(v, depth + 1)]),
  );
}

function scrubBreadcrumb(crumb: Breadcrumb): void {
  if (crumb.message) crumb.message = maskText(crumb.message);
  // The richest channel in the browser: the `fetch`/`xhr` crumb carries
  // `data.url` — every API call with its ids and query string — and the
  // `console` crumb carries `data.arguments`, i.e. the real arguments of every
  // console.log the app ever made.
  if (crumb.data) crumb.data = maskValue(crumb.data, 1) as Breadcrumb['data'];
}

export function scrubEvent(event: ErrorEvent, _hint: EventHint): ErrorEvent {
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    delete event.request.headers;
    // `url` is the field the browser populates, and it carries the query string
    // whole — masking only `query_string` left the same values in place beside it.
    if (event.request.url) event.request.url = maskText(event.request.url);
    if (event.request.query_string) {
      event.request.query_string = maskText(String(event.request.query_string));
    }
  }
  delete event.user;
  for (const value of event.exception?.values ?? []) {
    if (value.value) value.value = maskText(value.value);
  }
  if (event.message) event.message = maskText(event.message);
  for (const crumb of event.breadcrumbs ?? []) scrubBreadcrumb(crumb);
  if (event.extra) event.extra = maskValue(event.extra) as ErrorEvent['extra'];
  return event;
}

export const sharedSentryOptions = {
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  enabled: Boolean(process.env.NEXT_PUBLIC_SENTRY_DSN),
  environment: process.env.NODE_ENV,
  sendDefaultPii: false,
  tracesSampleRate: 0,
  beforeSend: scrubEvent,
};
